/**
 * Thrivia Embed SDK: the public embed API client.
 *
 * Purpose: the ONE place the bundle talks to `api.thrivia.io`. Two reads, matching the two shapes
 * of mount the scanner produces:
 *
 *   GET {apiBase}embed/events?ids=a,b,c&key=…&surface=…&platform=…   every event mount on the page
 *   GET {apiBase}embed/creators/:username/events?key=…&limit=…       one creator list mount
 *
 * Three contracts live here and each one was expensive to learn:
 *
 * 1. ONE batched call for every event mount on the page, not one per mount. A page with a card and
 *    three buttons makes a single request. That is what makes the endpoint's 60 second shared cache
 *    worth having, and it is the difference between one round trip and four on an organizer's
 *    homepage.
 * 2. Results are keyed by `card.id`, NEVER by array position. `getEventsByIds` runs a `$in` match
 *    and Mongo returns index order, which has no relationship to the order the ids were asked for.
 *    Zipping the response against the request array silently shows the wrong event on the wrong
 *    mount, and it does so only when the ids happen to sort differently, so it survives every
 *    single-mount test.
 * 3. `apiBase` always ends with exactly one slash (`index.ts` normalizes it) and paths are appended
 *    WITHOUT a leading one. This repo has shipped both halves of that convention wrong before.
 *
 * Failure posture, from the plan: unreachable, non-ok, or malformed all resolve to the same
 * `ok: false` result. Nothing throws out of here, nothing is retried, and the caller leaves the
 * organizer's baked permalink anchor exactly as it found it. The widget's worst day is a page that
 * still contains a working link to the event.
 *
 * `sid: null` in an otherwise healthy response is NOT a failure. It means the key does not trust
 * this origin yet, so the data renders and no telemetry is sent. See `track.ts`.
 *
 * No runtime dependencies, ES2019 output target.
 */

/**
 * One event card, exactly as `EmbedService.toCard` shapes it in the backend
 * (`thrivia-backend/src/services/embed/EmbedService.ts`). Kept structurally identical on purpose:
 * this is a wire contract between two repositories and a drift here renders wrong data rather than
 * failing.
 *
 * `url` is the canonical `https://www.thrivia.io/@handle/slug`. Both the `@` and the `www` are
 * load-bearing and Plan 1 shipped a Critical getting them wrong, so every link and every JSON-LD
 * `offers.url` uses this string verbatim and never rebuilds it from `handle` and `slug`.
 */
export interface EmbedEventCard {
  id: string;
  title: string;
  slug: string;
  handle: string;
  url: string;
  cover_photo: string | null;
  start_date: string | null;
  location: string | null;
  min_price: number | null;
  spots_left: number | null;
  state: "live" | "sold_out" | "ended" | "cancelled";
}

/** What every request needs in order to identify itself. Built once at boot. */
export interface RequestContext {
  /** Always ends with exactly one slash. Append paths without a leading slash. */
  apiBase: string;
  key: string;
  platform: string;
  debug: boolean;
}

export interface EmbedApiResult {
  /**
   * False for a network failure, a non-2xx status, a body that is not JSON, and a body whose shape
   * we do not recognise. The caller treats all four identically: leave the mount alone.
   */
  ok: boolean;
  /**
   * The attribution session for this response, or null when the API declined to mint one (an origin
   * the key has not learned, or the key's origin cap is reached). Null means render everything and
   * send nothing. It is never an error.
   */
  sid: string | null;
  /** Cards keyed by `card.id`. Never indexed by position. */
  cards: Map<string, EmbedEventCard>;
  /** The same cards in the order the API returned them, which is what the list surface renders. */
  list: EmbedEventCard[];
}

/**
 * Hard ceiling on ids in one request, mirroring `MAX_BATCH_IDS` in the backend's EmbedService.
 *
 * The server silently truncates a longer list, so a page with 60 mounts would render 50 cards and
 * leave 10 stuck on their skeletons with nothing in any log. Chunking here keeps the common page at
 * exactly one request while making the pathological page correct rather than partially blank.
 */
export const MAX_BATCH_IDS = 50;

/**
 * How long to wait for the API before giving up on a request.
 *
 * A hung connection is the one failure mode that the plan's "degrade to the permalink anchor"
 * posture does not cover on its own: without a bound, a stalled request leaves a skeleton on an
 * organizer's homepage forever. Promise.race rather than AbortController on purpose, because the
 * abort path needs a capability probe on older hosts and this needs none; the socket is left to the
 * browser, we simply stop waiting for it.
 */
const REQUEST_TIMEOUT_MS = 10000;

/** The failure value. A fresh object each time so a caller can never corrupt a shared one. */
function failed(): EmbedApiResult {
  return { ok: false, sid: null, cards: new Map(), list: [] };
}

function query(params: Record<string, string | number | null>): string {
  const parts: string[] = [];
  for (const name in params) {
    const value = params[name];
    if (value === null || value === "") continue;
    parts.push(encodeURIComponent(name) + "=" + encodeURIComponent(String(value)));
  }
  return parts.join("&");
}

/**
 * Validates and indexes a response body.
 *
 * Returns null for anything unrecognisable, which the caller turns into `ok: false`. Individual
 * malformed entries are skipped rather than failing the whole response: one bad row must not cost
 * an organizer every other card on the page.
 */
function parseBody(payload: unknown): EmbedApiResult | null {
  if (!payload || typeof payload !== "object") return null;
  const body = payload as { success?: unknown; events?: unknown; sid?: unknown };
  if (body.success !== true || !Array.isArray(body.events)) return null;

  const cards = new Map<string, EmbedEventCard>();
  const list: EmbedEventCard[] = [];
  for (let i = 0; i < body.events.length; i++) {
    const raw = body.events[i];
    if (!raw || typeof raw !== "object") continue;
    const card = raw as EmbedEventCard;
    if (typeof card.id !== "string" || card.id === "") continue;
    // Keyed by the card's OWN id. The response is in index order, not in the order we asked.
    cards.set(card.id, card);
    list.push(card);
  }

  return {
    ok: true,
    sid: typeof body.sid === "string" && body.sid !== "" ? body.sid : null,
    cards: cards,
    list: list,
  };
}

/** Issues one GET and normalizes every possible outcome into an EmbedApiResult. */
async function request(url: string, ctx: RequestContext): Promise<EmbedApiResult> {
  if (typeof fetch !== "function") return failed();

  // One debug line for every way this can fail, with the reason as its last argument.
  let why: unknown = "bad body";
  try {
    const response = await Promise.race([
      // `credentials: "omit"` matches the backend's `cors({ credentials: false })`. Sending
      // credentials would make the browser reject every response as a CORS error.
      fetch(url, { method: "GET", credentials: "omit", mode: "cors" }),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), REQUEST_TIMEOUT_MS)),
    ]);

    if (!response || !response.ok) {
      why = response ? response.status : "timeout";
    } else {
      const parsed = parseBody(await response.json());
      if (parsed) return parsed;
    }
  } catch (error) {
    // Network error, CORS rejection, a non-JSON body that json() choked on, anything at all.
    why = error;
  }
  if (ctx.debug) console.warn("[thrivia] api failed", url, why);
  return failed();
}

/**
 * Fetches every event card for one batch of mounts. Callers chunk at {@link MAX_BATCH_IDS}.
 *
 * `surface` is a single value for a whole batch because the endpoint mints one sid per response and
 * bakes the surface into it. On a page mixing surfaces the caller passes the mode of the first
 * event mount in document order; see the note in `index.ts`.
 */
export function fetchEventCards(
  ids: string[],
  surface: string,
  ctx: RequestContext
): Promise<EmbedApiResult> {
  if (ids.length === 0) return Promise.resolve(failed());
  const url =
    ctx.apiBase +
    "embed/events?" +
    query({ ids: ids.join(","), key: ctx.key, surface: surface, platform: ctx.platform });
  return request(url, ctx);
}

/** Fetches one creator's upcoming events. Creator-scoped, so it is never part of the batch. */
export function fetchCreatorEvents(
  username: string,
  limit: number,
  ctx: RequestContext
): Promise<EmbedApiResult> {
  const url =
    ctx.apiBase +
    "embed/creators/" +
    encodeURIComponent(username) +
    "/events?" +
    query({ key: ctx.key, surface: "list", platform: ctx.platform, limit: limit });
  return request(url, ctx);
}

/** Splits ids into request-sized chunks. Exported for the tests that pin the ceiling. */
export function chunkIds(ids: string[]): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += MAX_BATCH_IDS) {
    chunks.push(ids.slice(i, i + MAX_BATCH_IDS));
  }
  return chunks;
}
